export interface RuntimeProfilerOptions {
  enabled?: boolean;
  sampleLimit?: number;
}

export interface ProfileSeriesSnapshot {
  name: string;
  count: number;
  total: number;
  avg: number;
  min: number;
  max: number;
  p95: number;
  last: number;
}

export interface RuntimeProfileSnapshot {
  createdAt: string;
  uptimeMs: number;
  enabled: boolean;
  timings: ProfileSeriesSnapshot[];
  observations: ProfileSeriesSnapshot[];
}

interface Series {
  count: number;
  total: number;
  min: number;
  max: number;
  last: number;
  samples: number[];
}

function percentile(samples: readonly number[], p: number): number {
  if (samples.length === 0) return 0;
  const sorted = [...samples].sort((a, b) => a - b);
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  const weight = idx - lo;
  return sorted[lo] * (1 - weight) + sorted[hi] * weight;
}

function ensureSeries(map: Map<string, Series>, name: string): Series {
  let series = map.get(name);
  if (!series) {
    series = {
      count: 0,
      total: 0,
      min: Number.POSITIVE_INFINITY,
      max: Number.NEGATIVE_INFINITY,
      last: 0,
      samples: [],
    };
    map.set(name, series);
  }
  return series;
}

function updateSeries(series: Series, value: number, sampleLimit: number): void {
  if (!Number.isFinite(value)) return;
  series.count += 1;
  series.total += value;
  series.min = Math.min(series.min, value);
  series.max = Math.max(series.max, value);
  series.last = value;
  series.samples.push(value);
  if (series.samples.length > sampleLimit) {
    series.samples.splice(0, series.samples.length - sampleLimit);
  }
}

function snapshotSeries(map: Map<string, Series>): ProfileSeriesSnapshot[] {
  return [...map.entries()]
    .map(([name, series]) => ({
      name,
      count: series.count,
      total: series.total,
      avg: series.count > 0 ? series.total / series.count : 0,
      min: series.count > 0 ? series.min : 0,
      max: series.count > 0 ? series.max : 0,
      p95: percentile(series.samples, 0.95),
      last: series.last,
    }))
    .sort((a, b) => b.total - a.total || b.max - a.max || a.name.localeCompare(b.name));
}

export class RuntimeProfiler {
  private enabled: boolean;
  private readonly sampleLimit: number;
  private startedAt = performance.now();
  private readonly timings = new Map<string, Series>();
  private readonly observations = new Map<string, Series>();

  constructor(opts: RuntimeProfilerOptions = {}) {
    this.enabled = opts.enabled ?? false;
    this.sampleLimit = Math.max(8, opts.sampleLimit ?? 240);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  reset(): void {
    this.timings.clear();
    this.observations.clear();
    this.startedAt = performance.now();
  }

  recordTiming(name: string, durationMs: number): void {
    if (!this.enabled) return;
    updateSeries(ensureSeries(this.timings, name), durationMs, this.sampleLimit);
  }

  observe(name: string, value: number): void {
    if (!this.enabled) return;
    updateSeries(ensureSeries(this.observations, name), value, this.sampleLimit);
  }

  start(name: string): () => void {
    if (!this.enabled) return () => {};
    const startedAt = performance.now();
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.recordTiming(name, performance.now() - startedAt);
    };
  }

  measure<T>(name: string, fn: () => T): T {
    if (!this.enabled) return fn();
    const stop = this.start(name);
    try {
      return fn();
    } finally {
      stop();
    }
  }

  async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
    if (!this.enabled) return fn();
    const stop = this.start(name);
    try {
      return await fn();
    } finally {
      stop();
    }
  }

  snapshot(): RuntimeProfileSnapshot {
    return {
      createdAt: new Date().toISOString(),
      uptimeMs: performance.now() - this.startedAt,
      enabled: this.enabled,
      timings: snapshotSeries(this.timings),
      observations: snapshotSeries(this.observations),
    };
  }

  summaryLines(limit = 6): string[] {
    const timings = this.snapshot().timings.slice(0, Math.max(1, limit));
    if (timings.length === 0) {
      return [this.enabled ? 'collecting samples…' : 'profiling disabled'];
    }
    return timings.map(
      (row) =>
        `${row.name} avg ${row.avg.toFixed(2)} p95 ${row.p95.toFixed(2)} max ${row.max.toFixed(2)} n=${row.count}`,
    );
  }
}
